import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Upload, Loader2, Image as ImageIcon, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useContentActions } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Editable content schema — each entry is either a text field
   ({label, type: "text"|"textarea", default: string})
   or an image field ({label, type: "image", default: string}).
   Grouped by section for the accordion UI. */
const SCHEMA = [
  {
    id: "hero",
    title: "Hero",
    fields: [
      { key: "hero.overline", label: "Overline", type: "text", def: "Lincolnshire · Est. adults-only" },
      { key: "hero.title_pre", label: "Title first line", type: "text", def: "Slow down by the" },
      { key: "hero.title_em", label: "Title accent word", type: "text", def: "water’s edge." },
      { key: "hero.subtitle", label: "Subtitle paragraph", type: "textarea", def: "An award-winning, adult-only holiday park set across ten acres of landscaped countryside near Boston. Three lakeside lodges, two private carp lakes and dog-friendly pitches — all beneath the wide fenland sky." },
      { key: "hero.cta_primary", label: "Primary button label", type: "text", def: "Book your stay" },
      { key: "hero.cta_secondary", label: "Secondary button label", type: "text", def: "Enquire About Dates" },
      { key: "hero.image", label: "Background image (fallback)", type: "image", def: "" },
      { key: "hero.video", label: "Background video (MP4 URL) — plays on loop, silent", type: "text", def: "" },
    ],
  },
  {
    id: "about",
    title: "The Park (About)",
    fields: [
      { key: "about.overline", label: "Overline", type: "text", def: "The Park" },
      { key: "about.title", label: "Section title", type: "textarea", def: "A working farm turned into a place to breathe." },
      { key: "about.body1", label: "First paragraph", type: "textarea", def: "Walnut Lakes sits just off the A16, in a quiet fold of Lincolnshire fenland outside Boston..." },
      { key: "about.body2", label: "Second paragraph", type: "textarea", def: "We keep it small on purpose..." },
      { key: "about.image", label: "Section image", type: "image", def: "" },
      { key: "about.stat1_v", label: "Stat 1 value", type: "text", def: "10" },
      { key: "about.stat1_l", label: "Stat 1 label", type: "text", def: "Acres of countryside" },
      { key: "about.stat2_v", label: "Stat 2 value", type: "text", def: "2" },
      { key: "about.stat2_l", label: "Stat 2 label", type: "text", def: "Private carp lakes" },
      { key: "about.stat3_v", label: "Stat 3 value", type: "text", def: "3" },
      { key: "about.stat3_l", label: "Stat 3 label", type: "text", def: "Lakeside lodges" },
      { key: "about.stat4_v", label: "Stat 4 value", type: "text", def: "45" },
      { key: "about.stat4_l", label: "Stat 4 label", type: "text", def: "Camping pitches" },
    ],
  },
  {
    id: "stays",
    title: "Stays (section headings)",
    fields: [
      { key: "stays.overline", label: "Overline", type: "text", def: "Three lodges" },
      { key: "stays.title_pre", label: "Title first line", type: "text", def: "Named for the birds that" },
      { key: "stays.title_em", label: "Title accent line", type: "text", def: "watch you fish." },
      { key: "stays.body", label: "Intro paragraph", type: "textarea", def: "Each lodge sits on the water’s edge with its own deck..." },
    ],
  },
  {
    id: "heron",
    title: "Heron Lodge",
    fields: [
      { key: "lodges.heron.name", label: "Name", type: "text", def: "Heron Lodge" },
      { key: "lodges.heron.tag", label: "Tag line", type: "text", def: "Cosy · lake view" },
      { key: "lodges.heron.image", label: "Photo", type: "image", def: "" },
    ],
  },
  {
    id: "kingfisher",
    title: "Kingfisher Lodge",
    fields: [
      { key: "lodges.kingfisher.name", label: "Name", type: "text", def: "Kingfisher Lodge" },
      { key: "lodges.kingfisher.tag", label: "Tag line", type: "text", def: "Bright · waterside deck" },
      { key: "lodges.kingfisher.image", label: "Photo", type: "image", def: "" },
    ],
  },
  {
    id: "alpine",
    title: "Alpine Lodge",
    fields: [
      { key: "lodges.alpine.name", label: "Name", type: "text", def: "Alpine Lodge" },
      { key: "lodges.alpine.tag", label: "Tag line", type: "text", def: "Flagship · premium fit-out" },
      { key: "lodges.alpine.image", label: "Photo", type: "image", def: "" },
    ],
  },
  {
    id: "touring",
    title: "Camping & touring",
    fields: [
      { key: "touring.title_pre", label: "Title first line", type: "text", def: "Pitches from" },
      { key: "touring.title_em", label: "Title accent line", type: "text", def: "£24 a night." },
      { key: "touring.body", label: "Description", type: "textarea", def: "45 spacious pitches in total — 15 gravel hardstanding & 10 grass with 16A EHU, plus 20 non-electric grass tent pitches. Dogs welcome. EHU includes 15 kWh/day — extra use 33p/kWh via prepaid top-up card." },
      { key: "touring.image", label: "Photo", type: "image", def: "" },
    ],
  },
  {
    id: "fishing",
    title: "Fishing",
    fields: [
      { key: "fishing.overline", label: "Overline", type: "text", def: "The Fishing" },
      { key: "fishing.title_pre", label: "Title first line", type: "text", def: "Walnut & Daisy." },
      { key: "fishing.title_em", label: "Title accent line", type: "text", def: "Two lakes, plenty of carp." },
      { key: "fishing.body", label: "Description", type: "textarea", def: "Our two private waters are heavily stocked with mirror and common carp..." },
      { key: "fishing.day_ticket", label: "Day ticket price", type: "text", def: "£6" },
      { key: "fishing.image", label: "Photo", type: "image", def: "" },
    ],
  },
  {
    id: "contact",
    title: "Contact",
    fields: [
      { key: "contact.phone", label: "Phone", type: "text", def: "07982 844201" },
      { key: "contact.email", label: "Email", type: "text", def: "justina@walnutlakes.co.uk" },
      { key: "contact.address1", label: "Address line 1", type: "text", def: "Main Road, Algarkirk" },
      { key: "contact.address2", label: "Address line 2", type: "text", def: "Boston, Lincolnshire" },
      { key: "contact.address3", label: "Address line 3", type: "text", def: "Lincolnshire, PE20 2LQ" },
    ],
  },
  {
    id: "seo",
    title: "SEO & Google",
    fields: [
      { key: "seo.google_review_link", label: "Google review link — sent in post-stay review emails. Get it from Google Business Profile → “Ask for reviews”. Leave blank to use the fallback link.", type: "text", def: "" },
      { key: "seo.google_place_id", label: "Google Place ID (optional — opens the write-review box directly)", type: "text", def: "" },
    ],
  },
  {
    id: "sitemap",
    title: "Site Map (Policies page)",
    fields: [
      { key: "sitemap.image", label: "Official site plan image (optional — replaces the illustrated map)", type: "image", def: "" },
    ],
  },
];

// Utility to get/set nested values
const getVal = (obj, path) => path.split(".").reduce((a, p) => (a == null ? a : a[p]), obj);
const setVal = (obj, path, val) => {
  const parts = path.split(".");
  const clone = JSON.parse(JSON.stringify(obj || {}));
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    if (typeof cur[parts[i]] !== "object" || cur[parts[i]] === null) cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = val;
  return clone;
};

const ImageField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      const publicUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(publicUrl);
      toast.success("Image uploaded.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-24 h-16 rounded-lg bg-[#F5EFE6] border border-[#DDD1BE] overflow-hidden flex items-center justify-center flex-shrink-0">
          {value ? (
            <img src={value} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={20} className="text-[#B5A99E]" />
          )}
        </div>
        <div className="flex-1">
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-[#DDD1BE] rounded-lg bg-white text-sm cursor-pointer hover:bg-[#F5EFE6] transition">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading..." : "Upload new image"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={upload}
              disabled={uploading}
            />
          </label>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="ml-2 text-xs text-[#8C2E39] hover:underline"
            >
              Remove
            </button>
          )}
        </div>
      </div>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste a public image URL"
        className="bg-[#F5EFE6] h-10 rounded-lg text-xs"
      />
    </div>
  );
};

const ContentEditor = () => {
  const { content: current, refresh } = useContentActions();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ hero: true });

  useEffect(() => {
    setDraft(current || {});
  }, [current]);

  const change = useCallback((path, v) => {
    setDraft((prev) => setVal(prev, path, v));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, draft);
      toast.success("Content saved.");
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const resetSection = (fields) => {
    let next = draft;
    fields.forEach((f) => (next = setVal(next, f.key, "")));
    setDraft(next);
  };

  return (
    <div data-testid="content-editor" className="space-y-4">
      <div className="flex items-center justify-between bg-white border border-[#DDD1BE] rounded-2xl p-5">
        <div>
          <div className="overline text-[#6A5F58]">Editing live content</div>
          <div className="text-sm text-[#3A322E] mt-1">
            Changes go live immediately after saving. Leave a field blank to
            use the default. Uploaded images are stored securely on your site.
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-content"
          className="btn-primary font-brand tracking-[0.14em] disabled:opacity-60"
        >
          {saving ? (<><Loader2 size={14} className="animate-spin" /> Saving...</>) : "Save all changes"}
        </button>
      </div>

      {/* Website feature switches — instant save, no "Save all changes" needed */}
      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="feature-switches">
        <div>
          <div className="font-serif text-lg text-[#14100F]">Interactive pitch map booking</div>
          <div className="text-xs text-[#6A5F58] mt-0.5 max-w-md">
            Shows a "Pick Your Pitch" tab on the website — guests click Daisy Lake or Walnut Lake, choose their exact
            pitch (guaranteed theirs) and book it. Hidden never breaks anything — the classic booking stays as-is.
          </div>
        </div>
        <button
          type="button"
          onClick={async () => {
            const next = setVal(draft, "pitchmap.enabled", getVal(draft, "pitchmap.enabled") === "1" ? "" : "1");
            setDraft(next);
            try {
              await axios.put(`${API}/admin/content`, next);
              refresh();
              toast.success(getVal(next, "pitchmap.enabled") === "1" ? "Pitch map is now VISIBLE on the website." : "Pitch map hidden from the website.");
            } catch {
              toast.error("Couldn't save — try again.");
            }
          }}
          className={`relative inline-flex h-8 w-14 items-center rounded-full transition ${getVal(draft, "pitchmap.enabled") === "1" ? "bg-[#3B8C4E]" : "bg-[#DDD1BE]"}`}
          data-testid="toggle-pitchmap"
          aria-label="Toggle interactive pitch map"
        >
          <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${getVal(draft, "pitchmap.enabled") === "1" ? "translate-x-7" : "translate-x-1"}`} />
        </button>
      </div>

      {/* Aerial photo view switch — instant save. Hides the "Aerial" tab on the
          Pick-Your-Pitch page without deleting the photo or its pins. */}
      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="feature-switch-aerial">
        <div>
          <div className="font-serif text-lg text-[#14100F]">Aerial photo view</div>
          <div className="text-xs text-[#6A5F58] mt-0.5 max-w-md">
            Shows the real drone photo as a view option on the Pick-Your-Pitch map. Turn it off anytime — your photo
            and pin positions are kept, and guests fall back to the 3D model and site plan.
          </div>
        </div>
        <button
          type="button"
          onClick={async () => {
            const next = setVal(draft, "pitchmap.aerial_enabled", getVal(draft, "pitchmap.aerial_enabled") === "0" ? "1" : "0");
            setDraft(next);
            try {
              await axios.put(`${API}/admin/content`, next);
              refresh();
              toast.success(getVal(next, "pitchmap.aerial_enabled") === "0" ? "Aerial view hidden — guests see the 3D model and site plan." : "Aerial view is now VISIBLE on the pitch map.");
            } catch {
              toast.error("Couldn't save — try again.");
            }
          }}
          className={`relative inline-flex h-8 w-14 items-center rounded-full transition ${getVal(draft, "pitchmap.aerial_enabled") !== "0" ? "bg-[#3B8C4E]" : "bg-[#DDD1BE]"}`}
          data-testid="toggle-pitchmap-aerial"
          aria-label="Toggle aerial photo view"
        >
          <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${getVal(draft, "pitchmap.aerial_enabled") !== "0" ? "translate-x-7" : "translate-x-1"}`} />
        </button>
      </div>

      {/* Site plan view switch — instant save. Hides the "Site plan" tab on the
          Pick-Your-Pitch page; the admin map editor always keeps the layer. */}
      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="feature-switch-plan">
        <div>
          <div className="font-serif text-lg text-[#14100F]">Site plan view</div>
          <div className="text-xs text-[#6A5F58] mt-0.5 max-w-md">
            Shows the official site plan as a view option on the Pick-Your-Pitch map. Turn it off to show guests only
            the 3D model and aerial photo — the site plan stays available in the admin editor either way.
          </div>
        </div>
        <button
          type="button"
          onClick={async () => {
            const next = setVal(draft, "pitchmap.plan_enabled", getVal(draft, "pitchmap.plan_enabled") === "0" ? "1" : "0");
            setDraft(next);
            try {
              await axios.put(`${API}/admin/content`, next);
              refresh();
              toast.success(getVal(next, "pitchmap.plan_enabled") === "0" ? "Site plan hidden — guests see the 3D model and aerial photo." : "Site plan view is now VISIBLE on the pitch map.");
            } catch {
              toast.error("Couldn't save — try again.");
            }
          }}
          className={`relative inline-flex h-8 w-14 items-center rounded-full transition ${getVal(draft, "pitchmap.plan_enabled") !== "0" ? "bg-[#3B8C4E]" : "bg-[#DDD1BE]"}`}
          data-testid="toggle-pitchmap-plan"
          aria-label="Toggle site plan view"
        >
          <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${getVal(draft, "pitchmap.plan_enabled") !== "0" ? "translate-x-7" : "translate-x-1"}`} />
        </button>
      </div>

      {SCHEMA.map((section) => {
        const open = !!expanded[section.id];
        return (
          <div key={section.id} className="bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden">
            <button
              type="button"
              onClick={() => setExpanded((p) => ({ ...p, [section.id]: !p[section.id] }))}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-[#F5EFE6]/60 transition"
              data-testid={`section-toggle-${section.id}`}
            >
              <span className="font-serif text-xl text-[#14100F]">{section.title}</span>
              <span className="text-xs uppercase tracking-[0.24em] text-[#6A5F58]">
                {open ? "Collapse" : "Edit"}
              </span>
            </button>
            {open && (
              <div className="px-6 pb-6 pt-2 space-y-5 border-t border-[#DDD1BE]">
                {section.fields.map((f) => {
                  const v = getVal(draft, f.key) ?? "";
                  return (
                    <div key={f.key}>
                      <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">
                        {f.label}
                      </Label>
                      {f.type === "textarea" ? (
                        <Textarea
                          value={v}
                          onChange={(e) => change(f.key, e.target.value)}
                          placeholder={f.def}
                          className="mt-2 bg-[#F5EFE6] rounded-lg min-h-[80px]"
                          data-testid={`field-${f.key}`}
                        />
                      ) : f.type === "image" ? (
                        <div className="mt-2">
                          <ImageField
                            value={v}
                            onChange={(url) => change(f.key, url)}
                          />
                        </div>
                      ) : (
                        <Input
                          value={v}
                          onChange={(e) => change(f.key, e.target.value)}
                          placeholder={f.def}
                          className="mt-2 bg-[#F5EFE6] h-11 rounded-lg"
                          data-testid={`field-${f.key}`}
                        />
                      )}
                      {f.def && f.type !== "image" && (
                        <p className="mt-1.5 text-[10px] text-[#B5A99E]">
                          Default: {f.def.length > 80 ? f.def.slice(0, 80) + "…" : f.def}
                        </p>
                      )}
                    </div>
                  );
                })}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => resetSection(section.fields)}
                    className="text-xs text-[#8C2E39] hover:underline"
                  >
                    Reset section to defaults
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ContentEditor;
